import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { KTIcon } from '../../../../_metronic/helpers';
import { toast } from 'react-toastify';
import { get_active_projects, get_active_subprojects_of_project } from '../api/downloadAssetsApi';
import { update_license } from '../api/assetsApi';

interface UpdateLicenseModalProps {
  show: boolean;
  onHide: () => void;
  license: {
    id: number;
    license_id?: number;
    subproject_id?: number;
    project_id?: number;
    license_date?: string;
    license_text?: string;
    description?: string;
    license_file_path?: string;
  };
  onSuccess?: () => void;
}

interface ProjectItem {
  id: number;
  project_name: string;
}

interface SubprojectItem {
  id: number;
  subproject_name: string;
  project_id: number;
}

const UpdateLicenseModal: React.FC<UpdateLicenseModalProps> = ({
  show,
  onHide,
  license,
  onSuccess
}) => {
  const [projects, setProjects] = useState<ProjectItem[]>([]);
  const [subprojects, setSubprojects] = useState<SubprojectItem[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [isLoadingSubprojects, setIsLoadingSubprojects] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    project_id: license.project_id ? String(license.project_id) : '',
    subproject_id: license.subproject_id ? String(license.subproject_id) : '',
    license_date: license.license_date ? new Date(license.license_date).toISOString().split('T')[0] : '',
    license_text: license.license_text || license.description || '',
    license_file: null as File | null
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load projects on mount
  useEffect(() => {
    if (show) {
      loadProjects();
      // If license has project_id, load subprojects
      if (license.project_id) {
        loadSubprojects(license.project_id);
      }
    }
  }, [show, license.project_id]);

  const loadProjects = async () => {
    try {
      setIsLoadingProjects(true);
      const response = await get_active_projects();
      const apiProjects = response?.data?.active_projects || response?.data?.all_projects || response?.data?.projects || response?.data || [];
      const allProjects: ProjectItem[] = (Array.isArray(apiProjects) ? apiProjects : []).map((p: any) => ({
        id: Number(p.id),
        project_name: p.project_name || p.name || ''
      }));
      setProjects(allProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast.error('Failed to load projects');
    } finally {
      setIsLoadingProjects(false);
    }
  };

  const loadSubprojects = async (projectId: number | string) => {
    if (!projectId) {
      setSubprojects([]);
      return;
    }

    try {
      setIsLoadingSubprojects(true);
      const response = await get_active_subprojects_of_project(String(projectId));
      const apiSubprojects = response?.data?.active_subprojects_of_project || response?.data?.active_subprojects || response?.data?.subprojects || response?.data?.all_subprojects || response?.data || [];
      const allSubprojects: SubprojectItem[] = (Array.isArray(apiSubprojects) ? apiSubprojects : []).map((sp: any) => ({
        id: Number(sp.id),
        subproject_name: sp.subproject_name || sp.title || '',
        project_id: Number(projectId)
      }));
      setSubprojects(allSubprojects);
    } catch (error) {
      console.error('Error loading subprojects:', error);
      toast.error('Failed to load subprojects');
      setSubprojects([]);
    } finally {
      setIsLoadingSubprojects(false);
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value;
    setFormData(prev => ({ ...prev, project_id: projectId, subproject_id: '' }));
    if (projectId) {
      loadSubprojects(projectId);
    } else {
      setSubprojects([]);
    }
  };

  const handleSubprojectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData(prev => ({ ...prev, subproject_id: e.target.value }));
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, license_date: e.target.value }));
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData(prev => ({ ...prev, license_text: e.target.value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, license_file: file }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.subproject_id) {
      toast.error('Please select a subproject');
      return;
    }

    if (!formData.license_date) {
      toast.error('Please select a license date');
      return;
    }

    try {
      setIsSubmitting(true);

      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('license_id', String(license.license_id || license.id));
      formDataToSend.append('subproject_id', formData.subproject_id);
      formDataToSend.append('license_date', formData.license_date);

      if (formData.license_text) {
        formDataToSend.append('license_text', formData.license_text);
      }

      if (formData.license_file) {
        // Append file with the field name expected by API
        formDataToSend.append('license_file_path', formData.license_file);
      }

      await update_license(formDataToSend);

      toast.success('License updated successfully!');
      
      // Reset form
      setFormData({
        project_id: '',
        subproject_id: '',
        license_date: '',
        license_text: '',
        license_file: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      if (onSuccess) {
        onSuccess();
      }
      onHide();
    } catch (error: any) {
      console.error('Error updating license:', error);
      const errorMessage = error?.response?.data?.message || 
                          error?.response?.data?.error || 
                          error?.message || 
                          'Failed to update license';
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFormData({
        project_id: license.project_id ? String(license.project_id) : '',
        subproject_id: license.subproject_id ? String(license.subproject_id) : '',
        license_date: license.license_date ? new Date(license.license_date).toISOString().split('T')[0] : '',
        license_text: license.license_text || license.description || '',
        license_file: null
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      onHide();
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <KTIcon iconName="pencil" className="me-2" />
          Update License
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label className="required">Project</Form.Label>
            <Form.Select
              value={formData.project_id}
              onChange={handleProjectChange}
              disabled={isLoadingProjects || isSubmitting}
              required
            >
              <option value="">Select Project</option>
              {projects.map(project => (
                <option key={project.id} value={project.id}>
                  {project.project_name}
                </option>
              ))}
            </Form.Select>
            {isLoadingProjects && <div className="form-text">Loading projects...</div>}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required">Subproject</Form.Label>
            <Form.Select
              value={formData.subproject_id}
              onChange={handleSubprojectChange}
              disabled={!formData.project_id || isLoadingSubprojects || isSubmitting}
              required
            >
              <option value="">Select Subproject</option>
              {subprojects.map(subproject => (
                <option key={subproject.id} value={subproject.id}>
                  {subproject.subproject_name}
                </option>
              ))}
            </Form.Select>
            {isLoadingSubprojects && <div className="form-text">Loading subprojects...</div>}
            {formData.project_id && !isLoadingSubprojects && subprojects.length === 0 && (
              <div className="form-text text-muted">No subprojects available for this project</div>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="required">License Date</Form.Label>
            <Form.Control
              type="date"
              value={formData.license_date}
              onChange={handleDateChange}
              disabled={isSubmitting}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>License Text / Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.license_text}
              onChange={handleTextChange}
              disabled={isSubmitting}
              placeholder="Enter license text or description (optional)"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>License File</Form.Label>
            <Form.Control
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.txt"
              onChange={handleFileChange}
              disabled={isSubmitting}
            />
            <Form.Text className="text-muted">
              {formData.license_file 
                ? `Selected: ${formData.license_file.name}` 
                : license.license_file_path 
                  ? `Current: ${license.license_file_path}` 
                  : 'Upload a new license file (optional)'}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Updating...
              </>
            ) : (
              <>
                <KTIcon iconName="check" className="me-2" />
                Update License
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default UpdateLicenseModal;
